import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Assignment, Business, DirectionsRun, EmojiEvents, Forum, Groups, Home, MenuBook, Payment, Person, PlaylistAddCheck, SportsGymnastics, Star, SupportAgent, ViewList } from '@mui/icons-material';
import { List } from '@mui/material';
import { AccountBoxMultiple, CalendarCursor, ChartBoxOutline, ClipboardList, NewspaperVariantOutline } from 'mdi-material-ui';
import CollapsibleMenuItem from 'components/shared/menus/CollapsibleMenuItem';
import MenuItem from 'components/shared/menus/MenuItem';
import NestedMenuItem from 'components/shared/menus/NestedMenuItem';
import routes from 'common/routes';
import SidebarPanelContext from 'context/SidebarPanelContext';

function ClientDesktopMenu() {
  const { t } = useTranslation(['routes', 'pqrs']);

  // Rail colapsado: lo decide el panel host (desktop mini / mobile expandido).
  const { iconOnly: miniDrawer } = useContext(SidebarPanelContext);
  const platform = useSelector((state) => state.auth.user.platform);
  const isActivePlus = useSelector((state) => state.auth.user.is_active_plus);
  const hasOneOnOne = useSelector((state) => state.auth.user.has_one_on_one_sessions);
  const membershipStatus = useSelector((state) => state.auth.user.membership_status);
  const isGym = platform === 'otrix_gym';
  const isOtrixPlay = platform === 'otrix_play';
  const isInactive = membershipStatus === 'INACTIVE';

  return (
    <List>
      <MenuItem
        icon={<Home />}
        iconOnly={miniDrawer}
        itemKey="home"
        label={t('home', { ns: 'routes' })}
        path={routes.clientDashboard.path}
      />
      <MenuItem
        icon={<NewspaperVariantOutline />}
        iconOnly={miniDrawer}
        itemKey="news"
        label={t('news.list', { ns: 'routes' })}
        path={routes.listNews.path}
      />
      {isOtrixPlay && (
        <>
          <MenuItem
            exact
            icon={<SportsGymnastics />}
            iconOnly={miniDrawer}
            itemKey="otrix-play"
            label={t('play.home', { ns: 'routes', defaultValue: 'OTRIX Play' })}
            path={routes.otrixPlayHome.path}
          />
          <MenuItem
            icon={<PlaylistAddCheck />}
            iconOnly={miniDrawer}
            itemKey="play-routine"
            label={t('play.routine', { ns: 'routes', defaultValue: 'Mi rutina' })}
            path={routes.playRoutine.path}
          />
          {/* Entrenamiento: entra directo a lo que toca hoy (o a recuperación si es descanso),
              sin tener que arrancar la rutina desde otra pantalla. */}
          <MenuItem
            icon={<DirectionsRun />}
            iconOnly={miniDrawer}
            itemKey="play-workout"
            label={t('play.workout', { ns: 'routes', defaultValue: 'Entrenamiento' })}
            path={routes.playWorkout.path}
          />
          <MenuItem
            icon={<Forum />}
            iconOnly={miniDrawer}
            itemKey="play-forum"
            label={t('play.forum', { ns: 'routes', defaultValue: 'Foro de rutinas' })}
            path={routes.playForum.path}
          />
        </>
      )}
      {/* Agenda: clientes regulares, Active Plus, con sesiones 1:1 y OTRIX Play (su semana Play
          se ve aquí como cards por día). */}
      {((!isGym && !isOtrixPlay) || isActivePlus || hasOneOnOne || isOtrixPlay) && (
        <MenuItem
          icon={<CalendarCursor />}
          iconOnly={miniDrawer}
          itemKey="calendar"
          label={t('session.calendar', { ns: 'routes' })}
          path={routes.calendar.path}
        />
      )}
      {!isGym && !isOtrixPlay && (
        <MenuItem
          icon={<ViewList />}
          iconOnly={miniDrawer}
          itemKey="my-sessions"
          label={t('session.mySessions', { ns: 'routes' })}
          path={routes.clientSessions.path}
        />
      )}
      {isActivePlus && (
        <MenuItem
          icon={<Groups />}
          iconOnly={miniDrawer}
          itemKey="group-sessions"
          label={t('session.groupSessions', { ns: 'routes' })}
          path={routes.activePlusSessions.path}
        />
      )}
      <MenuItem
        icon={<MenuBook />}
        iconOnly={miniDrawer}
        itemKey="my-program"
        label={t('clientProgram', { ns: 'routes', defaultValue: 'Mi Programa' })}
        path={routes.clientProgram.path}
      />
      <MenuItem
        icon={<ChartBoxOutline />}
        iconOnly={miniDrawer}
        itemKey="performance"
        label={t('performance.list', { ns: 'routes' })}
        path={isOtrixPlay ? routes.playPerformance.path : routes.clientPerformances.path}
      />
      {/* Progreso (fotos de avance compartidas con el entrenador) va en LOS TRES canales:
          Active Plus, sesiones 1 a 1 y OTRIX Play. Antes `!isActivePlus` se lo escondía a Active
          Plus, que es justo el canal donde más sentido tiene: ahí el entrenador es quien
          acompaña el avance. La ruta nunca estuvo cerrada, solo faltaba la entrada del menú. */}
      {!isGym && (
        <MenuItem
          icon={<AccountBoxMultiple />}
          iconOnly={miniDrawer}
          itemKey="progress"
          label={t('progress.list', { ns: 'routes' })}
          path={routes.clientProgress.path}
        />
      )}
      {/* Ranking Global: solo para clientes NO Play. En Play desaparece (queda Ranking Play + Mi rango). */}
      {!isGym && !isOtrixPlay && (
        <MenuItem
          icon={<EmojiEvents />}
          iconOnly={miniDrawer}
          itemKey="ranking"
          label={t('gamification.globalRanking', { ns: 'routes' })}
          path={routes.globalRanking.path}
        />
      )}
      {!isGym && (
        <MenuItem
          icon={<Star />}
          iconOnly={miniDrawer}
          itemKey="gamification"
          label={t('gamification.myRank', { ns: 'routes' })}
          path={isOtrixPlay ? routes.playMyRank.path : routes.myGamificationProfile.path}
        />
      )}
      {isInactive && (
        <MenuItem
          icon={<ClipboardList />}
          iconOnly={miniDrawer}
          itemKey="my-plan"
          label={t('plan.myPlan', { ns: 'routes' })}
          path={routes.myPlanStatus.path}
        />
      )}
      <CollapsibleMenuItem
        icon={<Payment />}
        iconOnly={miniDrawer}
        label={t('payment.payments', { ns: 'routes' })}
        tourKey="payments"
        noLink>
        <NestedMenuItem
          label={t('payment.myPayments', { ns: 'routes', defaultValue: 'Mis pagos' })}
          path={routes.listClientPayments.client.path}
        />
        <NestedMenuItem
          label={t('payment.chargesHistory', { ns: 'routes', defaultValue: 'Historial de cobros' })}
          path={routes.clientChargesHistory.path}
        />
      </CollapsibleMenuItem>
      <MenuItem
        icon={<Business />}
        iconOnly={miniDrawer}
        itemKey="my-organization"
        label={t('myOrganization', { ns: 'routes', defaultValue: 'Mi organización' })}
        path={routes.joinOrganization.path}
      />
      <MenuItem
        icon={<Person />}
        iconOnly={miniDrawer}
        itemKey="profile"
        label={t('profile', { ns: 'routes' })}
        path={routes.clientProfile.path}
      />
      <MenuItem
        icon={<Assignment />}
        iconOnly={miniDrawer}
        itemKey="legal"
        label={t('legal.list', { ns: 'routes' })}
        path={routes.viewTC.path}
      />
      <MenuItem
        icon={<SupportAgent />}
        iconOnly={miniDrawer}
        itemKey="pqrs"
        label={t('menu', { ns: 'pqrs', defaultValue: 'Soporte (PQRS)' })}
        path={routes.myPqrs.path}
      />
    </List>
  );
}

export default ClientDesktopMenu;
